import { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Lock, X } from 'lucide-react';
import axios from 'axios';

const AddCardModal = ({ isOpen, onClose, onSuccess, isDark }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error('Stripe not loaded yet. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    setProcessing(true);

    try {
      const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
      
      // Create setup intent on backend
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/create-setup-intent`,
        { userEmail: user.email || 'test@shootzu.com' }
      );

      const { clientSecret, error } = response.data;

      if (error) {
        throw new Error(error);
      }

      // Confirm card setup
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user.full_name || user.fullName || 'Shootzu User'
          }
        }
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.setupIntent.status === 'succeeded') {
        toast.success('Card added successfully!');
        onSuccess();
        onClose();
      }

    } catch (error) {
      toast.error(error.message || 'Failed to add card');
      console.error('Add card error:', error);
    } finally {
      setProcessing(false);
    }
  };

  // Command Deck: Stripe CardElement must stay readable on the dark field.
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'ui-monospace, Menlo, Monaco, "SF Mono", monospace',
        '::placeholder': {
          color: 'rgba(255,255,255,0.4)',
        },
        iconColor: '#28C0D0',
      },
      invalid: {
        color: '#F87171',
        iconColor: '#F87171',
      },
    },
    hidePostalCode: false,
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[100]"
      style={{
        background: 'rgba(8,11,20,.72)',
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        padding: 20,
        fontFamily: 'var(--sz-font-body)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            background: 'linear-gradient(120deg,#572B7C,#78287C)',
            borderTopLeftRadius: 22,
            borderTopRightRadius: 22,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,.15)' }}
            >
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <h3 style={{ fontFamily: 'var(--sz-font-display)', fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: '-.4px' }}>
              Add Payment Method
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.18)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: processing ? 'not-allowed' : 'pointer',
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>
              Card Information
            </label>
            <div
              style={{
                padding: '14px 14px',
                borderRadius: 11,
                background: 'rgba(255,255,255,.04)',
                border: '1px solid rgba(255,255,255,.14)',
              }}
            >
              <CardElement options={cardElementOptions} />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Lock className="w-3 h-3" style={{ color: '#67C6B4' }} />
              <p className="text-xs" style={{ color: '#67C6B4' }}>Secured by Stripe • Card will be saved for future payments</p>
            </div>
          </div>

          <div
            style={{
              borderRadius: 11,
              padding: 12,
              background: 'rgba(40,192,208,.08)',
              border: '1px solid rgba(40,192,208,.25)',
            }}
          >
            <p className="text-xs" style={{ color: 'rgba(255,255,255,.7)' }}>
              ℹ️ Your card will be verified with a small authorisation that will be immediately released. No charges will be made.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              disabled={processing}
              variant="outline"
              className="flex-1"
              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.2)', color: '#fff' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!stripe || processing}
              className="flex-1 font-bold"
              style={{ background: '#28C0D0', color: '#0C1120', border: 'none' }}
            >
              {processing ? 'Adding Card...' : 'Add Card'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCardModal;
